import type {
  AssistantPlanSnapshot,
  ContractTypePlanControls,
  EditablePlanControls,
} from "@/components/assistant/AssistantPlanContext";
import { useAssistantPlanOptional } from "@/components/assistant/AssistantPlanContext";
import {
  formatWriteApprovalSummary,
  type WriteApprovalEntityKind,
} from "@/components/assistant/formatWriteApprovalSummary";
import { useEffect, useRef } from "react";

type Args = {
  kind: WriteApprovalEntityKind;
  activePending: {
    mode: "create" | "update";
    proposal: Record<string, unknown>;
    approvalReady: boolean;
  } | null;
  disabled: boolean;
  submitDisabled?: boolean;
  busy?: boolean;
  onCreate: () => void;
  contractTypePlan?: ContractTypePlanControls;
  editablePlan?: EditablePlanControls;
};

type SyncedKey = {
  kind: WriteApprovalEntityKind;
  mode: "create" | "update";
  proposal: Record<string, unknown>;
  approvalReady: boolean;
  disabled: boolean;
  submitDisabled: boolean;
  planTitle: string;
  contractTypePlan: ContractTypePlanControls | undefined;
  editablePlan: EditablePlanControls | undefined;
};

function sameSyncedKey(a: SyncedKey | null, b: SyncedKey): boolean {
  if (!a) return false;
  return (
    a.kind === b.kind &&
    a.mode === b.mode &&
    a.proposal === b.proposal &&
    a.approvalReady === b.approvalReady &&
    a.disabled === b.disabled &&
    a.submitDisabled === b.submitDisabled &&
    a.planTitle === b.planTitle &&
    a.contractTypePlan === b.contractTypePlan &&
    a.editablePlan === b.editablePlan
  );
}

export function useSyncAssistantPlan({
  kind,
  activePending,
  disabled,
  submitDisabled = false,
  busy = false,
  onCreate,
  contractTypePlan,
  editablePlan,
}: Args) {
  const planCtx = useAssistantPlanOptional();
  const setSnapshot = planCtx?.setSnapshot;
  const setOpen = planCtx?.setOpen;
  const onCreateRef = useRef(onCreate);
  onCreateRef.current = onCreate;
  const lastSyncedRef = useRef<SyncedKey | null>(null);

  useEffect(() => {
    if (!setSnapshot || !setOpen) return;

    if (!activePending) {
      if (!busy) {
        setOpen(false);
        setSnapshot(null);
        lastSyncedRef.current = null;
      }
      return;
    }

    const { headline } = formatWriteApprovalSummary(kind, activePending.proposal);
    const nextKey: SyncedKey = {
      kind,
      mode: activePending.mode,
      proposal: activePending.proposal,
      approvalReady: activePending.approvalReady,
      disabled,
      submitDisabled,
      planTitle: headline,
      contractTypePlan: kind === "contract-type" ? contractTypePlan : undefined,
      editablePlan: kind !== "contract-type" ? editablePlan : undefined,
    };

    if (sameSyncedKey(lastSyncedRef.current, nextKey)) return;

    const snapshot: AssistantPlanSnapshot = {
      kind: nextKey.kind,
      mode: nextKey.mode,
      proposal: nextKey.proposal,
      approvalReady: nextKey.approvalReady,
      disabled: nextKey.disabled,
      submitDisabled: nextKey.submitDisabled,
      onCreate: () => onCreateRef.current(),
      planTitle: nextKey.planTitle,
      contractTypePlan: nextKey.contractTypePlan,
      editablePlan: nextKey.editablePlan,
    };
    lastSyncedRef.current = nextKey;
    setSnapshot(snapshot);
  }, [
    setSnapshot,
    setOpen,
    kind,
    activePending,
    disabled,
    submitDisabled,
    busy,
    contractTypePlan,
    editablePlan,
  ]);
}
